import type {TimelineResizeHandle} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {MIN_ITEM_DURATION} from '@PosterWhiteboard/items/item/item.types';
import {positiveModulo, quantizeTimeSeconds} from '@Utils/math.util';

/**
 * The hybrid trim/loop continuum for a LOOPING video bar — one continuous handle
 * gesture per edge, piecewise by regime: while the bar shows at most one playback
 * pass, the handles edit real content (trim/reveal, source-bounded); past the
 * source, the bar loops. Callers gate on `isVideoLoopingEnabledForItem`; background
 * videos keep their span-derives-from-trim behavior elsewhere.
 *
 * Everything is a pure function of the ORIGINAL captured state plus the gesture's
 * total page-time delta, so a drag is reversible mid-gesture and the timeline
 * preview and the commit agree by construction (both feed the same delta).
 */

export interface HybridVideoResizeState {
  /** Source-trim start (source seconds). */
  startTime: number;
  /** Source-trim end (source seconds). */
  endTime: number;
  /** Full source length (source seconds). */
  duration: number;
  speed: number;
  /** Source-seconds into the trim window where the first (partial) pass starts. */
  loopStartOffset: number;
  onPageStartTime: number;
  onPageEndTime: number;
}

export interface HybridVideoResizeResult {
  startTime: number;
  endTime: number;
  loopStartOffset: number;
  onPageStartTime: number;
  onPageEndTime: number;
}

/**
 * Regime comparisons mix quantized stored times (millisecond grid, so up to ±0.0005s off)
 * with raw gesture spans — the epsilon must exceed that rounding quantum or the
 * tiled test oscillates every pointermove (a visible ghost/marker flicker).
 * Matches the loop-marker epsilon in item.library.ts.
 */
const REGIME_EPSILON_SECONDS = 0.01;

const getSafeSpeed = (speed: number): number => {
  return speed > 0 ? speed : 1;
};

/** Page-seconds of the bar's first (possibly partial) pass — the span at which the right edge exactly reaches the trim end. */
export const getVideoFirstPassExtent = (video: Pick<HybridVideoResizeState, 'startTime' | 'endTime' | 'speed' | 'loopStartOffset'>): number => {
  return (video.endTime - video.startTime - video.loopStartOffset) / getSafeSpeed(video.speed);
};

/** A bar is tiled when its span contains at least one loop restart — the regime where handles edit repeats, not content. */
export const isVideoBarTiled = (video: Pick<HybridVideoResizeState, 'startTime' | 'endTime' | 'speed' | 'loopStartOffset'>, spanDurationSeconds: number): boolean => {
  return spanDurationSeconds > getVideoFirstPassExtent(video) + REGIME_EPSILON_SECONDS;
};

/** The absolute page time at which a rightward reveal exhausts the source — where loops begin. */
export const getVideoSourceExhaustPageTime = (video: Pick<HybridVideoResizeState, 'startTime' | 'speed' | 'loopStartOffset' | 'duration' | 'onPageStartTime'>): number => {
  return video.onPageStartTime + (video.duration - video.startTime - video.loopStartOffset) / getSafeSpeed(video.speed);
};

/** The absolute page time at which a leftward reveal exhausts the source lead-in (first frame = source 0). */
export const getVideoSourceStartPageTime = (video: Pick<HybridVideoResizeState, 'startTime' | 'speed' | 'loopStartOffset' | 'onPageStartTime'>): number => {
  return video.onPageStartTime - (video.startTime + video.loopStartOffset) / getSafeSpeed(video.speed);
};

/** Rounding can land an offset exactly on the window length; a full pass is phase 0. */
const normalizeOffset = (offset: number, trimWindow: number): number => {
  const rounded = quantizeTimeSeconds(offset);
  return rounded >= trimWindow ? 0 : rounded;
};

const getRightHandleResize = (original: HybridVideoResizeState, deltaPageSeconds: number): HybridVideoResizeResult => {
  const {startTime, endTime, duration, loopStartOffset} = original;
  const speed = getSafeSpeed(original.speed);
  const span = original.onPageEndTime - original.onPageStartTime;
  const firstPassExtent = getVideoFirstPassExtent(original);
  const newSpan = Math.max(span + deltaPageSeconds, MIN_ITEM_DURATION);
  const base = {startTime, loopStartOffset, onPageStartTime: original.onPageStartTime, onPageEndTime: original.onPageStartTime + newSpan};

  // The page position (in span terms) of the trim end when the bar is span-only-covered.
  const contentEndForSpan = startTime + loopStartOffset + newSpan * speed;

  if (isVideoBarTiled(original, span)) {
    // Tiled bars edit repeats: span-only until the edge passes the last restart, then the drag continues into a trim.
    return {...base, endTime: newSpan >= firstPassExtent ? endTime : quantizeTimeSeconds(contentEndForSpan)};
  }

  if (deltaPageSeconds < 0) {
    // Trim the end. min() also absorbs an under-covered original (span shorter than
    // the window): the first inward pixel snaps the stored end to the visible extent.
    return {...base, endTime: Math.min(endTime, quantizeTimeSeconds(contentEndForSpan))};
  }

  if (newSpan <= firstPassExtent) {
    return {...base, endTime};
  }
  const exhaustExtent = (duration - startTime - loopStartOffset) / speed;
  if (newSpan <= exhaustExtent) {
    // Reveal the un-played tail — the edge drags real content out of the source.
    return {...base, endTime: quantizeTimeSeconds(Math.min(duration, contentEndForSpan))};
  }
  // Source exhausted: the window opens to the full tail and further growth loops.
  return {...base, endTime: duration};
};

const getLeftHandleResize = (original: HybridVideoResizeState, deltaPageSeconds: number): HybridVideoResizeResult => {
  const {startTime, endTime, loopStartOffset} = original;
  const speed = getSafeSpeed(original.speed);
  const span = original.onPageEndTime - original.onPageStartTime;
  const trimWindow = endTime - startTime;
  const newPageStartTime = Math.min(Math.max(original.onPageStartTime + deltaPageSeconds, 0), original.onPageEndTime - MIN_ITEM_DURATION);
  const clampedDelta = newPageStartTime - original.onPageStartTime;
  const base = {endTime, onPageStartTime: newPageStartTime, onPageEndTime: original.onPageEndTime};

  if (clampedDelta === 0 || trimWindow <= 0) {
    return {...base, startTime, loopStartOffset};
  }

  const phase = loopStartOffset + clampedDelta * speed;
  const tiled = isVideoBarTiled(original, span);

  if (clampedDelta > 0) {
    if (tiled) {
      const passDuration = trimWindow / speed;
      const firstPassExtent = getVideoFirstPassExtent(original);
      const lastRestart = firstPassExtent + Math.floor((span - REGIME_EPSILON_SECONDS - firstPassExtent) / passDuration) * passDuration;
      if (clampedDelta < lastRestart) {
        // A restart still sits right of the edge: advance the phase, boundaries fixed.
        return {...base, startTime, loopStartOffset: normalizeOffset(positiveModulo(phase, trimWindow), trimWindow)};
      }
    }
    // Inside the final (or only) pass: fold to a plain trimmed clip — the phase
    // becomes a real trim start and the offset clears.
    const foldedStartTime = quantizeTimeSeconds(startTime + positiveModulo(phase, trimWindow));
    return {...base, startTime: Math.min(foldedStartTime, quantizeTimeSeconds(endTime - MIN_ITEM_DURATION * speed)), loopStartOffset: 0};
  }

  if (tiled) {
    // Extending a tiled bar wraps — revealing would move every pass, which is not an edge-local edit.
    return {...base, startTime, loopStartOffset: normalizeOffset(positiveModulo(phase, trimWindow), trimWindow)};
  }

  const revealedSourceSeconds = -clampedDelta * speed;
  if (revealedSourceSeconds <= loopStartOffset) {
    // Unwind the phase toward the trim start.
    return {...base, startTime, loopStartOffset: quantizeTimeSeconds(loopStartOffset - revealedSourceSeconds)};
  }
  if (revealedSourceSeconds <= loopStartOffset + startTime) {
    // Reveal the un-played lead-in — the edge drags real content out of the source.
    return {...base, startTime: quantizeTimeSeconds(startTime + loopStartOffset - revealedSourceSeconds), loopStartOffset: 0};
  }
  // Lead-in exhausted: the window opens to [0, endTime] and further extension wraps
  // into loops at the front. Downstream frames stay fixed (content at the old bar
  // start remains the original first frame).
  const wrappedOffset = positiveModulo(-(revealedSourceSeconds - loopStartOffset - startTime), endTime);
  return {...base, startTime: 0, loopStartOffset: normalizeOffset(quantizeTimeSeconds(wrappedOffset), endTime)};
};

export const getHybridVideoResize = (original: HybridVideoResizeState, handle: TimelineResizeHandle, deltaPageSeconds: number): HybridVideoResizeResult => {
  if (deltaPageSeconds === 0) {
    const {startTime, endTime, loopStartOffset, onPageStartTime, onPageEndTime} = original;
    return {startTime, endTime, loopStartOffset, onPageStartTime, onPageEndTime};
  }
  if (handle === 'right') {
    return getRightHandleResize(original, deltaPageSeconds);
  }
  return getLeftHandleResize(original, deltaPageSeconds);
};
